// Only tree-shaking keeps `string.util`'s uuid-backed helpers out of a marketing bundle — the build's chunk grep checks for `uuid` alongside `fabric`.
import {isValidEmail} from '@Utils/string.util';
import type {
  QRAppStoreInput,
  QRCalendarEventInput,
  QREmailInput,
  QRPayloadCode,
  QRPayloadIssue,
  QRPayloadResult,
  QRPhoneInput,
  QRSmsInput,
  QRSocialInput,
  QRTextInput,
  QRUrlInput,
  QRVCardInput,
  QRWifiInput,
} from '@Libraries/qr-code/qr-code.types';
import {
  QR_EVENT_DESCRIPTION_MAX_LENGTH,
  QR_ISSUE_BLOCKS_DOWNLOAD,
  QR_PAYLOAD_MAX_BYTES,
  QR_WIFI_SECURITY_TOKENS,
  QRPayloadField,
  QRPayloadIssueCode,
  QRPayloadVariant,
  QRWifiSecurity,
} from '@Libraries/qr-code/qr-code.types';
import {
  encodeQueryValue,
  ensureHttpsProtocol,
  escapeStructuredTextValue,
  escapeWifiValue,
  formatUtcTimestamp,
  getContentHash,
  getPhoneNumberDigits,
  getUtcInstantForOffsetWallClock,
  getUtf8ByteLength,
  joinContentLines,
  normalisePhoneNumber,
  parseQRUrl,
  parseWallClock,
} from '@Libraries/qr-code/qr-payload-encoding.library';

/** Only WhatsApp needs a floor — see {@link getWhatsAppNumberIssues}. */
const MIN_WHATSAPP_NUMBER_DIGITS = 7;
const WHATSAPP_NUMBER_PREFIX_PATTERN = /^[+\d]/;
const WHATSAPP_LINK_BASE = 'https://wa.me/';
const TEL_URI_SCHEME = 'tel:';
const MAILTO_URI_SCHEME = 'mailto:';
const SMS_URI_SCHEME = 'SMSTO:';
const WIFI_URI_SCHEME = 'WIFI:';

const VCARD_VERSION = '3.0';
const ICAL_VERSION = '2.0';
const ICAL_PRODUCT_ID = '-//postermywall.com//QRCodeGenerator//EN';
const ICAL_UID_DOMAIN = 'postermywall.com';

const issue = (code: QRPayloadIssueCode, field: QRPayloadField | null): QRPayloadIssue => {
  return {code, field};
};

const toCode = (payload: string, variant: QRPayloadVariant = QRPayloadVariant.DEFAULT): QRPayloadCode => {
  return {variant, payload, byteLength: getUtf8ByteLength(payload)};
};

const getRequiredIssues = (value: string, field: QRPayloadField): QRPayloadIssue[] => {
  if (value.trim() !== '') {
    return [];
  }
  return [issue(QRPayloadIssueCode.REQUIRED, field)];
};

/**
 * The byte cap is checked once, here, against every code a type produced — so a type that emits two codes cannot slip an oversized one through, and no builder has to remember to
 * check.
 */
const toResult = (codes: [QRPayloadCode, ...QRPayloadCode[]], issues: QRPayloadIssue[]): QRPayloadResult => {
  const isOverCap = codes.some((code) => {
    return code.byteLength > QR_PAYLOAD_MAX_BYTES;
  });
  if (!isOverCap) {
    return {codes, issues};
  }
  return {codes, issues: [...issues, issue(QRPayloadIssueCode.OVER_BYTE_CAP, null)]};
};

const toSingleCodeResult = (payload: string, issues: QRPayloadIssue[]): QRPayloadResult => {
  return toResult([toCode(payload)], issues);
};

/**
 * The web-address type, which is the one a visitor reaches for to point at their own site — so it is held to the stricter grammar in {@link parseQRUrl} rather than the shared link
 * check above: a real domain, no whitespace, no scheme a browser will not open, and no credentials in the authority.
 */
const buildWebUrlPayload = (input: QRUrlInput): QRPayloadResult => {
  const parsedUrl = parseQRUrl(input.link, QRPayloadField.LINK);
  return toSingleCodeResult(parsedUrl.payload, parsedUrl.issues);
};

/**
 * A leading digit or `+` means the visitor typed a phone number for a one-to-one WhatsApp chat, which is the only social destination with no shareable link to paste. Exported so
 * Phase 3's logo detection reuses the rule rather than restating it.
 */
export const isWhatsAppChatNumber = (link: string): boolean => {
  return WHATSAPP_NUMBER_PREFIX_PATTERN.test(link.trim());
};

/**
 * The one number that does need checking. `wa.me` resolves its path segment as a full international number, so a short code or a local number with no country code reaches a
 * WhatsApp error page rather than a chat — unlike a dialer or messaging app, which accept both.
 */
const getWhatsAppNumberIssues = (phoneNumber: string, field: QRPayloadField): QRPayloadIssue[] => {
  const requiredIssues = getRequiredIssues(phoneNumber, field);
  if (requiredIssues.length > 0) {
    return requiredIssues;
  }
  if (getPhoneNumberDigits(phoneNumber).length < MIN_WHATSAPP_NUMBER_DIGITS) {
    return [issue(QRPayloadIssueCode.PHONE_TOO_SHORT, field)];
  }
  return [];
};

const buildWhatsAppChatPayload = (input: QRSocialInput): QRPayloadResult => {
  const trimmedMessage = input.whatsAppMessage.trim();
  const query = trimmedMessage === '' ? '' : `?text=${encodeQueryValue(trimmedMessage)}`;
  const payload = `${WHATSAPP_LINK_BASE}${getPhoneNumberDigits(input.link)}${query}`;

  return toSingleCodeResult(payload, getWhatsAppNumberIssues(input.link, QRPayloadField.LINK));
};

/**
 * One field covers every platform, because every platform is a URL — WhatsApp chat aside. Anything that is not a phone number is checked as a web address by the same strict
 * grammar the URL type uses, so a profile link is held to a real domain rather than merely looking link-shaped.
 */
const buildSocialPayload = (input: QRSocialInput): QRPayloadResult => {
  if (isWhatsAppChatNumber(input.link)) {
    return buildWhatsAppChatPayload(input);
  }
  return buildWebUrlPayload(input);
};

const buildTextPayload = (input: QRTextInput): QRPayloadResult => {
  return toSingleCodeResult(input.text, getRequiredIssues(input.text, QRPayloadField.TEXT_CONTENT));
};

const getEmailAddressIssues = (trimmedAddress: string): QRPayloadIssue[] => {
  const requiredIssues = getRequiredIssues(trimmedAddress, QRPayloadField.EMAIL_ADDRESS);
  if (requiredIssues.length > 0) {
    return requiredIssues;
  }
  if (!isValidEmail(trimmedAddress)) {
    return [issue(QRPayloadIssueCode.INVALID_EMAIL, QRPayloadField.EMAIL_ADDRESS)];
  }
  return [];
};

const buildEmailPayload = (input: QREmailInput): QRPayloadResult => {
  const trimmedAddress = input.emailAddress.trim();
  const trimmedSubject = input.subject.trim();
  const trimmedBody = input.body.trim();

  const parameters: string[] = [];
  if (trimmedSubject !== '') {
    parameters.push(`subject=${encodeQueryValue(trimmedSubject)}`);
  }
  if (trimmedBody !== '') {
    parameters.push(`body=${encodeQueryValue(trimmedBody)}`);
  }
  const query = parameters.length === 0 ? '' : `?${parameters.join('&')}`;

  return toSingleCodeResult(`${MAILTO_URI_SCHEME}${trimmedAddress}${query}`, getEmailAddressIssues(trimmedAddress));
};

/**
 * Whatever the visitor typed, minus spaces, and nothing else touched. Deliberately unvalidated beyond being present: a dialer accepts far more than an international number — short
 * codes, service numbers, extensions, a local number with no country code — so any shape rule here would turn away numbers the phone itself handles perfectly well.
 */
const buildPhonePayload = (input: QRPhoneInput): QRPayloadResult => {
  const payload = `${TEL_URI_SCHEME}${normalisePhoneNumber(input.phoneNumber)}`;
  return toSingleCodeResult(payload, getRequiredIssues(input.phoneNumber, QRPayloadField.PHONE_NUMBER));
};

/** Same rule as {@link buildPhonePayload}: the messaging app decides what a valid number is, not this. */
const buildSmsPayload = (input: QRSmsInput): QRPayloadResult => {
  const payload = `${SMS_URI_SCHEME}${normalisePhoneNumber(input.phoneNumber)}:${input.message}`;
  return toSingleCodeResult(payload, getRequiredIssues(input.phoneNumber, QRPayloadField.PHONE_NUMBER));
};

const getWifiIssues = (input: QRWifiInput, usesPassword: boolean): QRPayloadIssue[] => {
  const issues = getRequiredIssues(input.ssid, QRPayloadField.SSID);
  // Untrimmed on purpose, unlike the SSID above: a Wi-Fi password may legitimately be spaces.
  if (usesPassword && input.password === '') {
    issues.push(issue(QRPayloadIssueCode.REQUIRED, QRPayloadField.PASSWORD));
  }
  return issues;
};

/**
 * Segments are `KEY:value;` and the payload closes with one further `;`. A password segment is omitted entirely for an open network — an empty `P:;` makes some phones prompt for a
 * key.
 */
const buildWifiPayload = (input: QRWifiInput): QRPayloadResult => {
  const usesPassword = input.security !== QRWifiSecurity.NONE;

  const segments = [`T:${QR_WIFI_SECURITY_TOKENS[input.security]}`, `S:${escapeWifiValue(input.ssid)}`];
  if (usesPassword) {
    segments.push(`P:${escapeWifiValue(input.password)}`);
  }

  const payload = `${WIFI_URI_SCHEME}${segments.map((segment) => `${segment};`).join('')};`;
  return toSingleCodeResult(payload, getWifiIssues(input, usesPassword));
};

/** Every value is trimmed once here, so presence tests and emitted text can never disagree. */
const getVCardLines = (input: QRVCardInput): string[] => {
  const escape = escapeStructuredTextValue;
  const firstName = input.firstName.trim();
  const lastName = input.lastName.trim();
  const addressComponents = [input.street, input.city, input.region, input.postalCode, input.country].map((component) => {
    return component.trim();
  });

  const lines = ['BEGIN:VCARD', `VERSION:${VCARD_VERSION}`];
  lines.push(`N:${[escape(lastName), escape(firstName), '', '', ''].join(';')}`);
  lines.push(`FN:${escape(`${firstName} ${lastName}`.trim())}`);

  const optionalProperties: [string, string][] = [
    ['ORG', input.organisation.trim()],
    ['TITLE', input.jobTitle.trim()],
    ['TEL;TYPE=CELL', normalisePhoneNumber(input.mobileNumber)],
    ['TEL;TYPE=WORK', normalisePhoneNumber(input.workNumber)],
    ['TEL;TYPE=FAX', normalisePhoneNumber(input.faxNumber)],
    ['EMAIL', input.emailAddress.trim()],
    ['URL', input.website.trim() === '' ? '' : ensureHttpsProtocol(input.website)],
  ];
  optionalProperties.forEach(([property, value]) => {
    if (value !== '') {
      lines.push(`${property}:${escape(value)}`);
    }
  });

  const hasAddress = addressComponents.some((component) => {
    return component !== '';
  });
  if (hasAddress) {
    lines.push(`ADR;TYPE=WORK:${['', '', ...addressComponents.map(escape)].join(';')}`);
  }
  lines.push('END:VCARD');

  return lines;
};

const buildVCardPayload = (input: QRVCardInput): QRPayloadResult => {
  const issues = [...getRequiredIssues(input.firstName, QRPayloadField.FIRST_NAME), ...getRequiredIssues(input.lastName, QRPayloadField.LAST_NAME)];
  return toSingleCodeResult(joinContentLines(getVCardLines(input)), issues);
};

interface QRCalendarEventSchedule {
  startProperty: string;
  endProperty: string;
  isEndBeforeStart: boolean;
  /** Raised here rather than in {@link getCalendarEventIssues} because only parsing knows them. */
  scheduleIssues: QRPayloadIssue[];
}

/**
 * A half-filled pair is not a badly typed one. A date chosen from the calendar with the time still empty cannot be turned into an instant, and reporting that as an invalid date
 * told the visitor the date they had just picked was wrong.
 */
const isEitherPartBlank = (date: string, time: string): boolean => {
  return date.trim() === '' || time.trim() === '';
};

/**
 * A date or time the visitor has not finished typing stands in as {@link QRCalendarEventInput.generatedAt}, so the payload is always structurally valid iCalendar and the preview
 * keeps drawing. The accompanying issue is what stops anyone downloading the placeholder.
 */
const getScheduleIssue = (isMissing: boolean, hasBlankPart: boolean, field: QRPayloadField): QRPayloadIssue[] => {
  if (!isMissing) {
    return [];
  }
  return [issue(hasBlankPart ? QRPayloadIssueCode.REQUIRED : QRPayloadIssueCode.INVALID_DATE, field)];
};

/**
 * Every event is a timed one, converted to UTC so it lands at the right hour wherever it is scanned. A date or time still being typed falls back to a placeholder instant; the
 * issues it reports alongside are what stop that placeholder being downloaded.
 */
const getEventSchedule = (input: QRCalendarEventInput): QRCalendarEventSchedule => {
  const start = parseWallClock(input.startDate, input.startTime);
  const end = parseWallClock(input.endDate, input.endTime);
  const startInstant = start === null ? input.generatedAt : getUtcInstantForOffsetWallClock(start, input.utcOffsetMinutes);
  const endInstant = end === null ? input.generatedAt : getUtcInstantForOffsetWallClock(end, input.utcOffsetMinutes);

  return {
    startProperty: `DTSTART:${formatUtcTimestamp(startInstant)}`,
    endProperty: `DTEND:${formatUtcTimestamp(endInstant)}`,
    // `<` rather than `<=` because a zero-length event is legal.
    isEndBeforeStart: start !== null && end !== null && endInstant.getTime() < startInstant.getTime(),
    scheduleIssues: [
      ...getScheduleIssue(start === null, isEitherPartBlank(input.startDate, input.startTime), QRPayloadField.STARTS_AT),
      ...getScheduleIssue(end === null, isEitherPartBlank(input.endDate, input.endTime), QRPayloadField.ENDS_AT),
    ],
  };
};

/** Derived from the event's own details rather than randomly, so re-scanning the same code updates the calendar entry instead of adding a duplicate. */
const getEventUid = (input: QRCalendarEventInput, schedule: QRCalendarEventSchedule): string => {
  const identity = `${input.title.trim()}|${schedule.startProperty}|${schedule.endProperty}|${input.location.trim()}`;
  return `${getContentHash(identity)}@${ICAL_UID_DOMAIN}`;
};

const getCalendarEventLines = (input: QRCalendarEventInput, schedule: QRCalendarEventSchedule): string[] => {
  const trimmedLocation = input.location.trim();
  const trimmedDescription = input.description.trim();

  const lines = [
    'BEGIN:VCALENDAR',
    `VERSION:${ICAL_VERSION}`,
    `PRODID:${ICAL_PRODUCT_ID}`,
    'BEGIN:VEVENT',
    `UID:${getEventUid(input, schedule)}`,
    `DTSTAMP:${formatUtcTimestamp(input.generatedAt)}`,
    schedule.startProperty,
    schedule.endProperty,
    `SUMMARY:${escapeStructuredTextValue(input.title.trim())}`,
  ];
  if (trimmedLocation !== '') {
    lines.push(`LOCATION:${escapeStructuredTextValue(trimmedLocation)}`);
  }
  if (trimmedDescription !== '') {
    lines.push(`DESCRIPTION:${escapeStructuredTextValue(trimmedDescription)}`);
  }
  lines.push('END:VEVENT', 'END:VCALENDAR');

  return lines;
};

const getCalendarEventIssues = (input: QRCalendarEventInput, schedule: QRCalendarEventSchedule): QRPayloadIssue[] => {
  const issues = [...getRequiredIssues(input.title, QRPayloadField.TITLE), ...schedule.scheduleIssues];
  if (schedule.isEndBeforeStart) {
    issues.push(issue(QRPayloadIssueCode.END_BEFORE_START, QRPayloadField.ENDS_AT));
  }
  if (input.description.trim().length > QR_EVENT_DESCRIPTION_MAX_LENGTH) {
    issues.push(issue(QRPayloadIssueCode.DESCRIPTION_TOO_LONG, QRPayloadField.DESCRIPTION));
  }
  return issues;
};

const buildCalendarEventPayload = (input: QRCalendarEventInput): QRPayloadResult => {
  const schedule = getEventSchedule(input);
  return toSingleCodeResult(joinContentLines(getCalendarEventLines(input, schedule)), getCalendarEventIssues(input, schedule));
};

/**
 * A store link that was filled in, as its own code. One listing cannot serve both stores, so this is the one type that yields more than one code — and a blank field is simply a
 * store the visitor is not publishing to, not an error, so it contributes no code rather than an empty one.
 */
const getStoreCode = (link: string, variant: QRPayloadVariant, field: QRPayloadField): {code: QRPayloadCode; issues: QRPayloadIssue[]} | null => {
  if (link.trim() === '') {
    return null;
  }
  const parsedUrl = parseQRUrl(link, field);
  return {code: toCode(parsedUrl.payload, variant), issues: parsedUrl.issues};
};

/**
 * One code per store link given: both filled yields two, one filled yields one. Neither filled reports the App Store field as required and still returns a placeholder code,
 * because every result carries at least one and the preview has to have something to draw.
 */
const buildAppStorePayload = (input: QRAppStoreInput): QRPayloadResult => {
  const storeCodes = [
    getStoreCode(input.appStoreLink, QRPayloadVariant.APP_STORE, QRPayloadField.APP_STORE_LINK),
    getStoreCode(input.playStoreLink, QRPayloadVariant.PLAY_STORE, QRPayloadField.PLAY_STORE_LINK),
  ].filter((storeCode): storeCode is {code: QRPayloadCode; issues: QRPayloadIssue[]} => {
    return storeCode !== null;
  });

  const [firstStoreCode] = storeCodes;
  if (firstStoreCode === undefined) {
    return toResult([toCode('', QRPayloadVariant.APP_STORE)], [issue(QRPayloadIssueCode.REQUIRED, QRPayloadField.APP_STORE_LINK)]);
  }

  return toResult(
    [firstStoreCode.code, ...storeCodes.slice(1).map((storeCode) => storeCode.code)],
    storeCodes.flatMap((storeCode) => storeCode.issues)
  );
};

export {
  buildAppStorePayload,
  buildCalendarEventPayload,
  buildEmailPayload,
  buildPhonePayload,
  buildSmsPayload,
  buildSocialPayload,
  buildTextPayload,
  buildVCardPayload,
  buildWebUrlPayload,
  buildWifiPayload,
};

/** Whether anything is wrong that should stop a download, as opposed to merely warranting a warning. */
export const hasBlockingQRPayloadIssue = (result: QRPayloadResult): boolean => {
  return result.issues.some((payloadIssue) => {
    return QR_ISSUE_BLOCKS_DOWNLOAD[payloadIssue.code];
  });
};
